/**
 * @file SkuCodeLabelFields.tsx
 * @description Секция «Код и этикетка» диалога фасовки (phase19.22, W17-1,
 * W17-5b, ui-design §5): код склада только для чтения + шаблон этикетки.
 *
 * Шаблон: «Как у вида: Стандарт» (пусто → `labelTemplate: null`, берётся из
 * вида товара) или свой — Стандарт / Компакт / Мини. Контракт `value/onChange`
 * — как у `SkuStorageFields`.
 */

import { InternalCodeField } from "../../../../components/catalog/product-kinds/InternalCodeField";
import { Label } from "../../../../components/ui/label";
import { SelectField } from "../../../../components/ui/SelectField";
import type { LabelTemplate } from "../../../../lib/api-product-kinds";
import { LABEL_TEMPLATES, LABEL_TEMPLATE_META } from "../../../../lib/product-kind-format";

interface Props {
  /** Код склада; null — фасовка ещё не сохранена. */
  internalCode: string | null;
  /** "" — как у вида. */
  value: "" | LabelTemplate;
  onChange: (value: "" | LabelTemplate) => void;
  /** Шаблон вида товара; null — вид не задан (тогда «Стандарт»). */
  kindTemplate: LabelTemplate | null;
  disabled?: boolean;
}

export function SkuCodeLabelFields({
  internalCode,
  value,
  onChange,
  kindTemplate,
  disabled,
}: Props) {
  const inherited = LABEL_TEMPLATE_META[kindTemplate ?? "STANDARD"];
  const options = [
    {
      value: "",
      label: kindTemplate ? `Как у вида: ${inherited.label}` : `Как у вида: ${inherited.label} (вид не задан)`,
    },
    ...LABEL_TEMPLATES.map((t) => ({
      value: t,
      label: `${LABEL_TEMPLATE_META[t].label} · ${LABEL_TEMPLATE_META[t].size}`,
    })),
  ];

  return (
    <div>
      <div className="label-editorial mb-3">Код и этикетка</div>
      <div className="flex flex-col gap-3">
        <InternalCodeField code={internalCode} isNew={internalCode === null} />
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="sku-label-template">Шаблон этикетки</Label>
          <SelectField
            id="sku-label-template"
            ariaLabel="Шаблон этикетки"
            value={value}
            onChange={(v) => onChange(v as "" | LabelTemplate)}
            options={options}
            disabled={disabled}
          />
          <p className="text-xs text-on-surface-variant">
            Мини 20×20 — только DataMatrix, для бутылок: клеится на дно.
          </p>
        </div>
      </div>
    </div>
  );
}
